import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from '../pages/Auth/Login';
import Register from '../pages/Auth/Register';
import ForgotPassword from '../pages/Auth/ForgotPassword';
import ResetPassword from '../pages/Auth/ResetPassword';
import A2PVerification from '../pages/Auth/A2PVerification';
import Terms from '../pages/Legal/Terms';
import Privacy from '../pages/Legal/Privacy';
import ProtectedRoute from '../components/Auth/ProtectedRoute';
import BillingGuard from '../components/Auth/BillingGuard';
import AdminRouteGuard from '../components/Auth/AdminRouteGuard';
import { preloadConversationsPage } from '../utils/routePreload';

const Layout = lazy(() => import('../components/Layout/Layout'));
const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
const Contacts = lazy(() => import('../pages/Contacts/Contacts'));
const ContactDetails = lazy(() => import('../pages/Contacts/ContactDetails'));
const BulkActions = lazy(() => import('../pages/Contacts/BulkActions'));
const Conversations = lazy(preloadConversationsPage);
const Campaigns = lazy(() => import('../pages/Campaigns/Campaigns'));
const Workflows = lazy(() => import('../pages/Workflows/Workflows'));
const Calendar = lazy(() => import('../pages/Calendar/Calendar'));
const Reports = lazy(() => import('../pages/Reports/Reports'));
const ActivityLog = lazy(() => import('../pages/ActivityLog/ActivityLog'));
const Documents = lazy(() => import('../pages/Documents/Documents'));
const Billing = lazy(() => import('../pages/Billing/Billing'));
const Settings = lazy(() => import('../pages/Settings/Settings'));
const Admin = lazy(() => import('../pages/Admin/Admin'));
const ClientSubmissions = lazy(() => import('../pages/Admin/ClientSubmissions'));
const TelnyxFailures = lazy(() => import('../pages/Admin/TelnyxFailures'));
const Telnyx = lazy(() => import('../pages/Telnyx/Telnyx'));
const PowerDialer = lazy(() => import('../pages/PowerDialer/PowerDialer'));
const CallHistory = lazy(() => import('../pages/CallHistory/CallHistory'));
const Voicemails = lazy(() => import('../pages/Voicemails/Voicemails'));
const Analytics = lazy(() => import('../pages/Analytics/Analytics'));
const Tutorials = lazy(() => import('../pages/Tutorials/Tutorials'));
const Tags = lazy(() => import('../pages/Tags/Tags'));
const Templates = lazy(() => import('../pages/Templates/Templates'));
const ImportExportLog = lazy(() => import('../pages/ImportExportLog/ImportExportLog'));
const Integrations = lazy(() => import('../pages/Integrations/Integrations'));

export const RouteLoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
    <div className="text-center" role="status" aria-live="polite">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
      <p className="mt-4 text-gray-600 dark:text-gray-400">Loading page...</p>
    </div>
  </div>
);

const AppRoutes: React.FC = () => (
  <Suspense fallback={<RouteLoadingFallback />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/a2p-verification" element={<A2PVerification />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      <Route path="/" element={
        <ProtectedRoute>
          <BillingGuard>
            <Layout />
          </BillingGuard>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="contacts/:id" element={<ContactDetails />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="contacts/bulk/actions" element={<BulkActions />} />
        <Route path="import-export-log" element={<ImportExportLog />} />
        <Route path="conversations" element={<Conversations />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="workflows" element={<Workflows />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="reports" element={<Reports />} />
        <Route path="activity-log" element={<ActivityLog />} />
        <Route path="documents" element={<Documents />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />
        <Route path="phone-numbers" element={<Telnyx />} />
        <Route path="power-dialer" element={<PowerDialer />} />
        <Route path="call-history" element={<CallHistory />} />
        <Route path="voicemails" element={<Voicemails />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="tutorials" element={<Tutorials />} />
        <Route path="tags" element={<Tags />} />
        <Route path="templates" element={<Templates />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="admin" element={
          <AdminRouteGuard>
            <Admin />
          </AdminRouteGuard>
        } />
        <Route path="admin/submissions" element={
          <AdminRouteGuard>
            <ClientSubmissions />
          </AdminRouteGuard>
        } />
        <Route path="admin/telnyx-failures" element={
          <AdminRouteGuard>
            <TelnyxFailures />
          </AdminRouteGuard>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
